import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, AlertCircle, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

interface WhiteLabelSettings {
  brand_name?: string;
  logo_url?: string;
  favicon_url?: string;
  primary_color: string;
  secondary_color: string;
  custom_css?: string;
  hide_powered_by: boolean;
  custom_domain?: string;
}

interface WhiteLabelResponse {
  white_label: WhiteLabelSettings;
}

const PLAN_GATE_PLANS = ['agency', 'pro'];

export const WhiteLabelPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<WhiteLabelSettings>({
    brand_name: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#3b82f6',
    secondary_color: '#1e40af',
    custom_css: '',
    hide_powered_by: false,
    custom_domain: '',
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Check if user's plan allows white label
  const isPlanAllowed = user?.plan && PLAN_GATE_PLANS.includes(user.plan);

  // Fetch white label settings
  const { data: settingsData, isLoading } = useQuery<WhiteLabelResponse, Error>({
    queryKey: ['whiteLabel', user?.team_id],
    queryFn: async () => {
      if (!user?.team_id) {
        throw new Error('Team not found');
      }
      const response = await fetch(`/api/teams/${user.team_id}/white-label`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch white label settings');
      }
      return response.json() as Promise<WhiteLabelResponse>;
    },
    enabled: !!(isPlanAllowed && user?.team_id),
  });

  // Load settings into form
  useEffect(() => {
    if (settingsData?.white_label) {
      const settings = settingsData.white_label;
      setFormData({
        brand_name: settings.brand_name || '',
        logo_url: settings.logo_url || '',
        favicon_url: settings.favicon_url || '',
        primary_color: settings.primary_color || '#3b82f6',
        secondary_color: settings.secondary_color || '#1e40af',
        custom_css: settings.custom_css || '',
        hide_powered_by: settings.hide_powered_by || false,
        custom_domain: settings.custom_domain || '',
      });
      setLogoPreview(settings.logo_url || null);
      setHasChanges(false);
    }
  }, [settingsData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: WhiteLabelSettings) => {
      if (!user?.team_id) {
        throw new Error('Team not found');
      }
      const response = await fetch(`/api/teams/${user.team_id}/white-label`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save white label settings');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whiteLabel'] });
      toast.success(t('whitelabel_saved', 'White label settings updated successfully'));
      setHasChanges(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('whitelabel_save_error', 'Failed to update white label settings'));
    },
  });

  const handleInputChange = (field: keyof WhiteLabelSettings, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);

    // Update logo preview
    if (field === 'logo_url' && typeof value === 'string' && value) {
      setLogoPreview(value);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveMutation.mutateAsync(formData);
  };

  if (!isPlanAllowed) {
    return (
      <div className="bg-gray-50 min-h-screen px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-start gap-4 mb-6">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('whitelabel_plan_gate', 'White label is available on Agency and Pro plans')}
                </h2>
                <p className="text-gray-600 mt-2">
                  {t('whitelabel_upgrade_prompt', 'Upgrade your plan to unlock white label customization')}
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <a
                href="/billing"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {t('whitelabel_upgrade_button', 'Upgrade Plan')}
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen px-6 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Palette size={32} className="text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              {t('whitelabel_title', 'White Label Settings')}
            </h1>
          </div>
          <p className="text-gray-600">
            {t('whitelabel_subtitle', 'Customize the branding and appearance of your workspace')}
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
                <div className="h-12 bg-gray-100 rounded-lg" />
              </div>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {/* Brand Name */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <label htmlFor="brand-name" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('whitelabel_brand_name', 'Brand Name')}
              </label>
              <input
                id="brand-name"
                type="text"
                value={formData.brand_name || ''}
                onChange={e => handleInputChange('brand_name', e.target.value)}
                placeholder={t('whitelabel_brand_name_placeholder', 'e.g., My Agency')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Logo URL */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <label htmlFor="logo-url" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('whitelabel_logo_url', 'Logo URL')}
              </label>
              <input
                id="logo-url"
                type="url"
                value={formData.logo_url || ''}
                onChange={e => handleInputChange('logo_url', e.target.value)}
                placeholder={t('whitelabel_logo_url_placeholder', 'https://example.com/logo.png')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {logoPreview && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {t('whitelabel_logo_preview', 'Logo Preview')}
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center border border-gray-200">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-48 max-h-24 object-contain"
                      onError={() => setLogoPreview(null)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Favicon URL */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <label htmlFor="favicon-url" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('whitelabel_favicon_url', 'Favicon URL')}
              </label>
              <input
                id="favicon-url"
                type="url"
                value={formData.favicon_url || ''}
                onChange={e => handleInputChange('favicon_url', e.target.value)}
                placeholder={t('whitelabel_favicon_url_placeholder', 'https://example.com/favicon.ico')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              {/* Primary Color */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <label htmlFor="primary-color" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('whitelabel_primary_color', 'Primary Color')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="primary-color"
                    type="color"
                    value={formData.primary_color}
                    onChange={e => handleInputChange('primary_color', e.target.value)}
                    className="w-16 h-12 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={e => handleInputChange('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <label htmlFor="secondary-color" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('whitelabel_secondary_color', 'Secondary Color')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="secondary-color"
                    type="color"
                    value={formData.secondary_color}
                    onChange={e => handleInputChange('secondary_color', e.target.value)}
                    className="w-16 h-12 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={e => handleInputChange('secondary_color', e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Custom CSS */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <label htmlFor="custom-css" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('whitelabel_custom_css', 'Custom CSS')}
              </label>
              <textarea
                id="custom-css"
                value={formData.custom_css || ''}
                onChange={e => handleInputChange('custom_css', e.target.value)}
                placeholder={t('whitelabel_custom_css_placeholder', 'Enter custom CSS rules here')}
                rows={6}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-2">
                Tip: Use CSS variables like --color-primary and --color-secondary
              </p>
            </div>

            {/* Hide Powered By */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start gap-3">
                <input
                  id="hide-powered-by"
                  type="checkbox"
                  checked={formData.hide_powered_by}
                  onChange={e => handleInputChange('hide_powered_by', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 mt-1 cursor-pointer"
                />
                <label htmlFor="hide-powered-by" className="flex-1 cursor-pointer">
                  <p className="font-semibold text-gray-900">
                    {t('whitelabel_hide_powered_by', 'Hide "Powered by" attribution')}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t('whitelabel_hide_powered_by_desc', 'Remove PlugD branding from the interface')}
                  </p>
                </label>
              </div>
            </div>

            {/* Custom Domain */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <label htmlFor="custom-domain" className="block text-sm font-semibold text-gray-900 mb-2">
                {t('whitelabel_custom_domain', 'Custom Domain')}
              </label>
              <input
                id="custom-domain"
                type="text"
                value={formData.custom_domain || ''}
                onChange={e => handleInputChange('custom_domain', e.target.value)}
                placeholder={t('whitelabel_custom_domain_placeholder', 'e.g., app.myagency.com')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-2">
                Note: You&apos;ll need to configure DNS records separately
              </p>
            </div>

            {/* Save Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={!hasChanges || saveMutation.isPending}
                className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                  !hasChanges || saveMutation.isPending
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {saveMutation.isPending ? (
                  <>
                    <span className="inline-block mr-2 animate-spin">⌛</span>
                    {t('whitelabel_saving', 'Saving...')}
                  </>
                ) : (
                  t('whitelabel_save', 'Save Settings')
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
